import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeyUtils } from '../lib/queryKeys';
import ConfirmationDialog from './ConfirmationDialog';
import { useToast } from './Toast';

const InstitutionManager = ({ onSyncComplete, refreshKey }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: null });

  // Fetch institutions on mount and when refreshKey changes
  useEffect(() => {
    fetchInstitutions();
  }, [refreshKey]);

  const fetchInstitutions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPlaidInstitutions();
      setInstitutions(data.institutions || []);
    } catch (err) {
      console.error('Error fetching institutions:', err);
      setError(err.message || 'Failed to load institutions');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (institutionId) => {
    try {
      setSyncing({ ...syncing, [institutionId]: true });
      setError(null);

      const data = await api.syncPlaidTransactions(institutionId, {
        startDate: null,
        endDate: null,
        skipCategorization: false,
      });
      
      // Refresh institutions list to update sync status
      await fetchInstitutions();

      // Invalidate all transaction-related queries to refresh the UI
      queryKeyUtils.getTransactionRelatedKeys().forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      
      // Also invalidate by prefix patterns to catch all related queries
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['daily-spending'] });
      queryClient.invalidateQueries({ queryKey: ['budget'] });

      if (onSyncComplete) {
        onSyncComplete(data);
      }
    } catch (err) {
      console.error('Error syncing transactions:', err);
      setError(err.message || 'Failed to sync transactions');
    } finally {
      setSyncing({ ...syncing, [institutionId]: false });
    }
  };

  const handleDelete = (institutionId, institutionName) => {
    setDeleteConfirm({ isOpen: true, id: institutionId, name: institutionName });
  };

  const confirmDelete = async () => {
    const { id, name } = deleteConfirm;
    setDeleteConfirm({ isOpen: false, id: null, name: null });

    try {
      setError(null);
      await api.deletePlaidInstitution(id);

      // Refresh institutions list
      await fetchInstitutions();
      showToast(`Successfully disconnected ${name}`, 'success');
    } catch (err) {
      console.error('Error deleting institution:', err);
      setError(err.message || 'Failed to disconnect institution');
      showToast(err.message || 'Failed to disconnect institution', 'error');
    }
  };

  const formatDate = (date) => {
    if (!date || date === null || date === undefined) return 'Never';
    
    // Handle string "null" or "undefined"
    if (typeof date === 'string' && (date === 'null' || date === 'undefined')) {
      return 'Never';
    }
    
    let d;
    
    try {
      // Handle Firestore Timestamp objects (if they somehow make it through)
      if (date.toDate && typeof date.toDate === 'function') {
        d = date.toDate();
      }
      // Handle serialized Firestore timestamps (from JSON API responses)
      else if (date.seconds !== undefined && typeof date.seconds === 'number') {
        const milliseconds = date.seconds * 1000;
        const nanoseconds = date.nanoseconds || 0;
        d = new Date(milliseconds + nanoseconds / 1000000);
      }
      // Handle Date objects
      else if (date instanceof Date) {
        d = date;
      }
      // Handle ISO strings or other date strings (server now sends ISO strings)
      else if (typeof date === 'string') {
        d = new Date(date);
      }
      // Fallback: try to create a date from whatever we have
      else {
        d = new Date(date);
      }
      
      // Check if date is valid
      if (!d || isNaN(d.getTime())) {
        console.warn('Invalid date value:', date);
        return 'Never';
      }
      
      const dateStr = d.toLocaleDateString();
      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      // Double-check we didn't get "Invalid Date" strings
      if (dateStr === 'Invalid Date' || timeStr === 'Invalid Date') {
        console.warn('Date formatting returned Invalid Date:', date);
        return 'Never';
      }
      
      return dateStr + ' ' + timeStr;
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Never';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading && institutions.length === 0) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin inline-block mr-2">⏳</div>
        <span>Loading institutions...</span>
      </div>
    );
  }

  if (institutions.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-gray-600 text-sm">No connected institutions. Connect a bank account to get started.</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {institutions.map((institution) => (
          <div
            key={institution.id}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800">
                  {institution.institution_name}
                </h3>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Status:</span>{' '}
                    <span
                      className={`px-2 py-1 rounded text-xs ${getStatusColor(
                        institution.sync_status
                      )}`}
                    >
                      {institution.sync_status || 'pending'}
                    </span>
                  </div>
                  {institution.last_sync_at && (
                    <div>
                      <span className="font-medium">Last synced:</span>{' '}
                      {formatDate(institution.last_sync_at)}
                    </div>
                  )}
                  {institution.last_sync_error && (
                    <div className="text-red-600 text-xs">
                      Error: {institution.last_sync_error}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Accounts:</span>{' '}
                    {institution.accounts?.length || 0}
                  </div>
                  <div>
                    <span className="font-medium">Environment:</span>{' '}
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {institution.environment || 'sandbox'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ml-4 flex flex-col gap-2">
                <button
                  onClick={() => handleSync(institution.id)}
                  disabled={syncing[institution.id]}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    syncing[institution.id]
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {syncing[institution.id] ? (
                    <>
                      <span className="animate-spin inline-block mr-1">⏳</span>
                      Syncing...
                    </>
                  ) : (
                    'Sync Transactions'
                  )}
                </button>
                <button
                  onClick={() => handleDelete(institution.id, institution.institution_name)}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white"
                >
                  Disconnect
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: null })}
        onConfirm={confirmDelete}
        title="Disconnect Institution"
        message={`Are you sure you want to disconnect ${deleteConfirm.name}? This action cannot be undone.`}
        confirmText="Disconnect"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
};

export default InstitutionManager;

